import clsx from 'clsx'
import { forwardRef, type HTMLAttributes } from 'react'

type SpinnerSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl'
type SpinnerVariant =
    | 'default'
    | 'primary'
    | 'primary-inverted'
    | 'simple'
    | 'simple-inverted'
    | 'success'
    | 'warning'
    | 'danger'
type SpinnerSpeed = '1s' | '500ms' | '250ms' | '150ms'

type SpinnerProps = HTMLAttributes<HTMLDivElement> & {
    size?: SpinnerSize
    variant?: SpinnerVariant
    backgroundClass?: string
    speed?: SpinnerSpeed
}

const Spinner = forwardRef<HTMLDivElement, SpinnerProps>((props, ref) => {
    const { variant = 'default', className, size = 'sm', speed = '1s', ...otherProps } = props

    const SpinnerSize = {
        xs: 'h-2.5 w-2.5 border-[1px]',
        sm: 'size-4 border-[2px]',
        md: 'size-6 border-[3px]',
        lg: 'size-8 border-[4px]',
        xl: 'size-12 border-[6px]',
        xxl: 'size-20 border-[10px]',
    }

    const SpinnerVariant: Record<SpinnerVariant, string> = {
        default: 'border-[currentColor] border-t-transparent',
        primary: 'border-t-primary-600 dark:border-t-primary-500 border-primary-200 dark:border-primary-900',
        'primary-inverted': 'dark:border-t-primary-600 border-t-primary-500 dark:border-primary-200 border-primary-900',
        success: 'border-t-success-600 dark:border-t-success-500 border-success-200 dark:border-success-900',
        warning: 'border-t-warning-600 dark:border-t-warning-500 border-warning-200 dark:border-warning-900',
        danger: 'border-t-danger-600 dark:border-t-danger-500 border-danger-200 dark:border-danger-900',
        simple: 'border-primary-600 border-t-transparent',
        'simple-inverted': 'border-primary-50 border-t-transparent',
    }

    return (
        <div
            className={clsx(
                SpinnerSize[size],
                SpinnerVariant[variant],
                'inline-block animate-spin rounded-full',
                className,
            )}
            style={{ animationDuration: speed }}
            role="status"
            aria-label="loading"
            ref={ref}
            {...otherProps}
        >
            <span className="sr-only">Loading...</span>
        </div>
    )
})

Spinner.displayName = 'Spinner'

export { Spinner, type SpinnerProps }
